/**
 * Recomputes document upload visibility and auth-letter state for a CPT row.
 * @module updateRowDataForDocWithUploadIcon
 */
import { CptCodesTableRowModel } from "../../../../../data-model/cptCodesTableModel";
import { AuthOnFile } from "../../../../../data-model/medicalPaOrdersModel";
import { MedicalPaOrdersAuthStatus } from "../../../../../enums/medicalPaOrdersAuthStatus";
import { CptCodeWithDetailsKeys } from "../../../../../enums/tableColumnKeys";
import { getAllDocsForSingleJCodeFromGeneralOrderDetails } from "../../../../../utils/getAllDocsForSingleJCode";
import { checkAuthLetterUploadStatus } from "./checkAuthLetterUploadStatus";

/**
 * Resolves `auth_letter_review_status` for a J-code row from auth-on-file entries.
 *
 * @param jCodeId - J-code record id
 * @param authOnFileDetails - Auth-on-file payload containing nested J-codes
 * @returns Review status string when present
 */
const getAuthLetterReviewStatusForJCode = (
  jCodeId: string,
  authOnFileDetails: AuthOnFile,
): string | undefined => {
  const entry = (
    authOnFileDetails?.entries ?? authOnFileDetails?.auth_entries
  )?.find((e) => e.j_codes?.some((j) => j.id === jCodeId));
  const status = entry?.j_codes?.find(
    (j) => j.id === jCodeId,
  )?.auth_letter_review_status;
  return status ?? undefined;
};

/**
 * Refreshes the `DocWithUploadIcon` column using the same rules as table generation:
 * visibility for Auth on File, upload detection via {@link checkAuthLetterUploadStatus}.
 *
 * @param rowData - Row after PA status or related edits
 * @param authOnFileDetails - Auth-on-file documents for the order
 * @returns Row with updated document cell metadata
 */
export const updateRowDataForDocWithUploadIcon = (
  rowData: CptCodesTableRowModel,
  authOnFileDetails: AuthOnFile,
) => {
  const currentStatusId =
    rowData[CptCodeWithDetailsKeys.PaRequired]?.currentStatusId ?? "";

  const existingDocData = rowData[CptCodeWithDetailsKeys.DocWithUploadIcon];
  const allDocs = getAllDocsForSingleJCodeFromGeneralOrderDetails(
    existingDocData.jCodeId,
    authOnFileDetails,
  );
  const authLetterReviewStatus =
    getAuthLetterReviewStatusForJCode(
      existingDocData.jCodeId,
      authOnFileDetails,
    ) ?? existingDocData.authLetterReviewStatus;

  return {
    ...rowData,
    [CptCodeWithDetailsKeys.DocWithUploadIcon]: {
      ...existingDocData,
      isVisible: currentStatusId === MedicalPaOrdersAuthStatus.AuthOnFile,
      isUploaded: checkAuthLetterUploadStatus(currentStatusId, allDocs ?? []),
      authStatus: currentStatusId,
      authLetterReviewStatus,
    },
  };
};
